import React, { useEffect, useState, useCallback } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import Icon from '@mui/material/Icon';

import DialogBase from './DialogBase';
import { InfraPlan } from '../../../models/plan/plan';
import { plannenService } from '../../../services/plannen.service';
import { ListParams } from '../../../models/listParams';

const PAGE_LIMIT = 20;

const DialogPortfolioAddPlans: React.FC<any> = ({
  open,
  portfolio,
  onClose,
  onSubmit,
}) => {

  const [plannen, setPlannen] = useState<InfraPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<InfraPlan | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const fetchPlannen = useCallback(
    async (searchText: string, nextOffset: number) => {
      try {
        setLoading(true);

        const params: ListParams = {
          searchText,
          offset: nextOffset,
          limit: PAGE_LIMIT,
          sortBy: 'PlanNaam asc',
        };

        const data = await plannenService.getPlannen(params);

        // console.log('Fetched plannen:', data);

        const fetched = data?.items ?? [];

        // append if offset > 0, else reset
        setPlannen((prev) =>
          nextOffset === 0 ? fetched : [...prev, ...fetched]
        );

        // if we received < limit, no more pages
        setHasMore(fetched.length === PAGE_LIMIT);

        setOffset(nextOffset + PAGE_LIMIT);
      }
      catch (e) {
        console.error(e);
      }
      finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!open) return;

    setOffset(0);
    setHasMore(true);
    fetchPlannen(inputValue, 0);
  }, [inputValue, open, fetchPlannen]);

  const onInputChange = (
    _event: React.SyntheticEvent,
    newValue: string,
    reason: string
  ) => {
    if (reason === 'input') {
      setInputValue(newValue);
    }
  };

  const handleScroll = (event: any) => {
    const listboxNode = event.currentTarget;

    const bottomReached =
      listboxNode.scrollTop + listboxNode.clientHeight >=
      listboxNode.scrollHeight - 1;

    if (bottomReached && hasMore && !loading) {
      fetchPlannen(inputValue, offset);
    }
  };

  const handleSubmit = () => { 
    if (!selectedPlan) return;

    onSubmit(selectedPlan?.infplId); 

    setInputValue('');
    setSelectedPlan(null);
    onClose();
  };

  return (
    <DialogBase
      open={open}
      title={`Plannen uitnodigen voor ${portfolio?.name}`}
      contentText="U gaat een plan uitnodigen voor het afstemmingsdossier."
      onClose={onClose}
      onConfirm={handleSubmit}
      confirmButtonText="Verzenden"
      confirmButtonIcon={<Icon>send</Icon>}
    >
      <Autocomplete
        options={plannen}
        getOptionLabel={(plan) => `${plan.planNaam} - ${plan.infplOrganizationName}`}
        onInputChange={onInputChange}
        value={selectedPlan}
        onChange={(_e, value) => setSelectedPlan(value)}
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Plannen zoeken"
            variant="filled"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        ListboxProps={{ onScroll: handleScroll }}
      />
    </DialogBase>
  );
};

export default DialogPortfolioAddPlans;
